import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { mediaUrl } from "../../lib/api";
import { mmss } from "../../lib/chat";

export default function AudioPlayer({ src, duration, mine, testid = "audio-player" }) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [total, setTotal] = useState(duration || 0);

  useEffect(() => {
    const a = ref.current;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => Number.isFinite(a.duration) && a.duration > 0 && setTotal(a.duration);
    const onEnd = () => { setPlaying(false); setTime(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    a.addEventListener("pause", () => setPlaying(false));
    a.addEventListener("play", () => setPlaying(true));
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => (ref.current.paused ? ref.current.play().catch(() => {}) : ref.current.pause());
  const seek = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (total) ref.current.currentTime = ((e.clientX - r.left) / r.width) * total;
  };
  const pct = total ? Math.min(100, (time / total) * 100) : 0;

  return (
    <div className="flex w-56 max-w-full items-center gap-3 py-1" data-testid={testid}>
      <audio ref={ref} src={mediaUrl(src)} preload="metadata" />
      <button onClick={toggle} aria-label={playing ? "pause" : "play"} data-testid={`${testid}-toggle`}
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform active:scale-90 ${mine ? "bg-white text-brand" : "bg-brand text-white"}`}>
        {playing ? <Pause className="h-4 w-4 fill-current" /> : <Play className="ml-0.5 h-4 w-4 fill-current" />}
      </button>
      <div className="min-w-0 flex-1">
        <div onClick={seek} className={`h-1.5 cursor-pointer overflow-hidden rounded-full ${mine ? "bg-white/35" : "bg-navy/15"}`} data-testid={`${testid}-progress`}>
          <div className={`h-full rounded-full ${mine ? "bg-white" : "bg-brand"}`} style={{ width: `${pct}%` }} />
        </div>
        <p className={`mt-1 text-[11px] font-semibold tabular-nums ${mine ? "text-white/85" : "text-slate-500"}`} data-testid={`${testid}-time`}>
          {playing || time ? `${mmss(time)} / ${mmss(total)}` : mmss(total)}
        </p>
      </div>
    </div>
  );
}
